import { Dispatch, SetStateAction, useEffect, useState } from 'react';

/**
 * A hook for reading and writing a single value to `sessionStorage`.
 *
 * Values are serialized with `JSON.stringify`/`JSON.parse`, so any
 * JSON-serializable type can be used as `T`.
 *
 * Safe to call during SSR as all access is guarded by a
 * `typeof window === 'undefined'` check and will return `null`/no-op
 * on the server.
 *
 * @template T - The type of the value stored under the given key.
 * @param key - The `sessionStorage` key to read from and write to.
 * @returns A tuple containing the stored value (or `null` if
 *   missing/unavailable) and a setter function.
 */

export const SessionStorageKey = {
  BOOKING_FLOW_RETURN_URL: 'bookingFlowReturnUrl',
  PREVIOUS_PAGE_URL: 'previousPageUrl',
} as const;

const useSessionStorage = <T>(key: string) => {
  const readValue = (): T | null => {
    if (typeof window === 'undefined') return null;

    try {
      const item = window.sessionStorage.getItem(key);

      return item ? (JSON.parse(item) as T) : null;
    } catch {
      return null;
    }
  };

  const [value, setValue] = useState<T | null>(() => readValue());

  useEffect(() => {
    setValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      if (value === null) {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      }
    } catch {
      // sessionStorage unavailable
    }
  }, [key, value]);

  return [value, setValue] as [T | null, Dispatch<SetStateAction<T | null>>];
};

export default useSessionStorage;
